import { useState, useContext, useEffect } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  Grid,
  Button,
  Dialog,
  DialogContent,
  Typography,
  TextField,
  Checkbox,
  Stack,
  MenuItem,
  Select,
  FormControl
} from "@mui/material";
import { useMutation } from "@apollo/client";
import "../../Style/dialogstyle.scss";
import { AuthContext } from "../../Context/AuthContext";
import { DialogTitleComp } from "../../Utils/Component";
import { SelectUser } from "../Include/DynamicSelect";
import { useGetNextNumberingOfSecretary } from "../../Hooks/useGetNextNumberingOfSecretary";
import { CREATE_SECRETARY_IN_MANDATE, UPDATE_SECRETARY_IN_MANDATE } from "../../Schema/Secretarait";

const getPartyFromLocal = () => {
  const data = JSON.parse(window.localStorage.getItem("partyId"));
  return data ? data : { id: "", title: "" };
};

const setPartyFromLocal = (value) => {
  window.localStorage.setItem("partyId", JSON.stringify(value));
};

export default function SecretaryInMandateForm({
  open,
  editData,
  setRefetch,
  handleClose,
  dialogTitle,
}) {
  // Change Language 
  const { t, setAlert } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const partyData = getPartyFromLocal();
  const [createSecretaryInMandate] = useMutation(CREATE_SECRETARY_IN_MANDATE, {
    onCompleted: ({ createSecretaryInMandate }) => {
      setLoading(false);
      if (createSecretaryInMandate?.isSuccess === true) {
        setAlert(true, "success", createSecretaryInMandate?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", createSecretaryInMandate?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  const [updateSecretaryInMandate] = useMutation(UPDATE_SECRETARY_IN_MANDATE, {
    onCompleted: ({ updateSecretaryInMandate }) => {
      setLoading(false);
      if (updateSecretaryInMandate?.isSuccess === true) {
        setAlert(true, "success", updateSecretaryInMandate?.message);
        handleClose();
        resetForm();
        setRefetch();
      } else {
        setAlert(true, "error", updateSecretaryInMandate?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  //fromik
  const CheckValidation = Yup.object().shape({
    numbering: Yup.number().nullable().required(t("required")),
    userId: Yup.object().shape({
      id: Yup.string().required(t("required")),
      title: Yup.string(),
    }),
    roleInSecretariat: Yup.string().required(t("required"))
  });

  const formik = useFormik({
    initialValues: {
      numbering: null,
      userId: {id: "", title: ""},
      roleInSecretariat: "member",
    },
    validationSchema: CheckValidation,
    onSubmit: async (values) => {
      setLoading(true);

      if (dialogTitle === "Update") {
        updateSecretaryInMandate({
          variables: {
            id: editData?._id,
            input: {
              numbering: parseFloat(values?.numbering),
              userId: values?.userId?.id,
              roleInSecretariat: values?.roleInSecretariat,
            },
          },
        });
      } else {
        createSecretaryInMandate({
          variables: {
            input: {
              numbering: parseFloat(values?.numbering),
              userId: values?.userId?.id,
              roleInSecretariat: values?.roleInSecretariat,
            },
          },
        });
      }
    },
  });

  const {
    values,
    errors,
    touched,
    handleSubmit,
    setFieldValue,
    getFieldProps,
    resetForm,
  } = formik;



  useEffect(() => {
    if (editData) {
      setFieldValue("numbering", editData?.numbering);
      setFieldValue("roleInSecretariat", editData?.roleInSecretariat);

      setFieldValue("userId", {
        id: editData?.userId?._id,
        title:
          editData?.userId?.lastNameKh +
          " " +
          editData?.userId?.firstNameKh,
      });
    }
  }, [editData, open]);


  const { refetch } = useGetNextNumberingOfSecretary({
    setFieldValue,
    dialogTitle,

  });

  useEffect(() => {
    refetch();
  }, [open]);

  useEffect(() => {
    if (dialogTitle === "Create") {
      setFieldValue("fromParty", partyData);
    }
  }, [open, dialogTitle, values?.fromParty?.id]);

  return (
    <Dialog open={open} className="dialog-container" maxWidth="xs" fullWidth>
      <DialogTitleComp
        title={
          dialogTitle === "Create"
            ? t("modal_title_create_candidate")
            : t("modal_title_update_candidate")
        }
        classTitle={"dialog-title"}
        classIcon={"close-icon"}
        handleClose={handleClose}
      />

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container rowSpacing={2} columnSpacing={3}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_numbering")}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  className="text-field"
                  placeholder={t("thead_numbering")}
                  {...getFieldProps("numbering")}
                  error={Boolean(touched.numbering && errors.numbering)}
                  helperText={touched.numbering && errors.numbering}
                />
              </Grid>

              <Grid item xs={12}>
                <SelectUser
                  quickCreateName={"Secretary"}
                  role={["Secretary"]}
                  buttonAdd={true}
                  onChange={(val) => setFieldValue("userId", val)}
                  value={values?.userId}
                  error={Boolean(
                    touched.userId?.id && errors.userId?.id
                  )}
                  helperText={touched.userId?.id && errors.userId?.id}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_role")}
                </Typography>
                <FormControl fullWidth className="search-field" size="small">
                  <Select
                    value={values?.roleInSecretariat}
                    onChange={(e) => setFieldValue("roleInSecretariat", e?.target.value)}
                  >
                    <MenuItem value={"president"}>President</MenuItem>
                    <MenuItem value={"vice_president"}>Vice President</MenuItem>
                    <MenuItem value={"member"}>Member</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                {loading ? (
                  <Button className={t("class-btn-create")} fullWidth>
                    {t("loading")}
                  </Button>
                ) : (
                  <Button
                    className={t("class-btn-create")}
                    fullWidth
                    onClick={handleSubmit}
                  >
                    {dialogTitle === "Create" ? t("create") : t("update")}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}
